import React, { useEffect, useRef } from 'react';

function Skills() {
  const sectionRef = useRef(null);

  const skillCategories = [
    {
      title: "Languages",
      icon: "💻",
      items: ["Java", "Python", "JavaScript", "C/C++", "SQL", "Dart", "Solidity", "HTML5/CSS3"]
    },
    {
      title: "Backend & Distributed",
      icon: "⚙️",
      items: ["Spring Boot", "FastAPI", "Node.js", "Express.js", "Flask", "RESTful APIs", "RabbitMQ", "Kafka (Learning)"]
    },
    {
      title: "Databases & Caching",
      icon: "🗄️",
      items: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "Firebase", "Database Schema Design"]
    },
    {
      title: "Cloud & DevOps",
      icon: "☁️",
      items: ["AWS", "Docker", "CI/CD Pipelines", "Git & GitHub", "Linux Systems", "Infrastructure Automation"]
    },
    {
      title: "Frontend Development",
      icon: "🎨",
      items: ["React.js", "Tailwind CSS", "React Native", "Flutter", "Responsive Web Design"]
    },
    {
      title: "Applied AI & ML",
      icon: "🧠",
      items: ["TensorFlow", "PyTorch", "Scikit-learn", "OpenCV", "Deepfake Detection", "Neural Networks"]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = Array.from(entry.target.querySelectorAll('.skill-card'));
            cards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-scale-in');
                card.classList.remove('opacity-0');
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  return (
    <section id="skills" className="py-24 md:py-32" ref={sectionRef}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-sm font-bold text-accent uppercase tracking-wider">My Skills</span>
          <h2 className="text-4xl md:text-5xl font-bold text-primary mt-2 mb-4 relative inline-block">
            Technical Expertise
            <span className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-24 h-1.5 bg-accent rounded-full"></span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto mt-8 text-gray-600 dark:text-gray-300">
            A comprehensive overview of the programming languages, frameworks, databases, and developer tools I work with.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {skillCategories.map((category, index) => (
            <div 
              key={index} 
              className="skill-card bg-white dark:bg-gray-800 rounded-2xl shadow-card hover:shadow-hover border border-gray-100 dark:border-gray-700 p-8 transition-all duration-300 transform opacity-0 relative overflow-hidden group hover:-translate-y-1"
            >
              {/* Top border accent line */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary to-accent"></div>
              
              <div className="flex items-center space-x-4 mb-6">
                <span className="text-3xl p-3 bg-primary/5 dark:bg-primary/20 rounded-xl">{category.icon}</span>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-accent-light transition-colors duration-300">
                  {category.title}
                </h3>
              </div>

              <div className="flex flex-wrap gap-2.5">
                {category.items.map((item, itemIdx) => (
                  <span 
                    key={itemIdx}
                    className="px-3.5 py-1.5 bg-gray-50 dark:bg-gray-700/50 hover:bg-primary hover:text-white dark:hover:bg-accent dark:hover:text-gray-950 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-full transition-all duration-300 border border-gray-100 dark:border-gray-700/60 shadow-sm cursor-default"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* What I Value Subtitle */}
        <div className="mt-16 text-center">
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
            * I choose tools based on the problem, not current trends. I value clean code, reproducible environments, and robust documentation.
          </p>
        </div>
      </div>
    </section>
  );
}

export default Skills;